import moment from 'moment';

type RowData = { [key: string]: any };

interface ColumnOptions {
  field: string;
  props?: any;
  rowIndex: number;
  frozenRow?: boolean;
}

/**
 * A generic body template for PrimeReact DataTable columns that displays a dash ('-')
 * for falsy values (e.g., null, undefined, '').
 * It correctly displays the number 0 if it's the value.
 * @param rowData The data for the current row.
 * @param options The column options, including the field name.
 * @returns A JSX element with the value or a dash.
 */
export const DefaultBodyTemplate = (
  rowData: RowData,
  { field }: ColumnOptions,
) => {
  const { [field]: value } = rowData;
  if (value === 0) {
    return <div>0</div>;
  }
  return <div>{value || '-'}</div>;
};

/**
 * A body template for date columns in a PrimeReact DataTable.
 * It formats the date using moment.js and displays a dash ('-') for empty values.
 * @param rowData The data for the current row.
 * @param options The column options, including the field name.
 * @returns A JSX element with the formatted date or a dash.
 */
export const DateBodyTemplate = (
  rowData: RowData,
  { field }: ColumnOptions,
) => {
  const { [field]: value } = rowData;
  return <div>{value ? moment(value).format('DD-MM-yyyy HH:mm') : '-'}</div>;
};

/**
 * A body template for input type columns in a PrimeReact DataTable.
 * Maps known input type values to display labels,
 * defaulting to 'с глюкометра' for empty values.
 * @param rowData The data for the current row.
 * @param options The column options, including the field name.
 * @returns A JSX element with the mapped input type label.
 */
export const InputTypeBodyTemplate = (
  rowData: RowData,
  { field }: ColumnOptions,
) => {
  const { [field]: value } = rowData;
  const inputTypeMap: Record<string, string> = {
    AUTO: 'с глюкометра',
    MANUAL: 'ручной',
  };
  if (!value) {
    return <div>{inputTypeMap.AUTO}</div>;
  }
  return <div>{inputTypeMap[value] ?? value}</div>;
};
